import { useEffect, useMemo } from 'react';
import { Outlet, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Theme } from '@astryxdesign/core/theme';
import { LinkProvider } from '@astryxdesign/core/Link';
import { ToastViewport, useToast } from '@astryxdesign/core/Toast';
import { Button } from '@astryxdesign/core/Button';
import { RouterLink } from '@/components/RouterLink';
import { ErrorState } from '@/components/QueryState';
import { toApiError } from '@/lib/api/errors';
import { env } from '@/lib/env';
import { registerStudioWorker } from '@/lib/pwa/register';
import { readShell } from '@/lib/shell';
import { studioTheme } from '@/theme/studioTheme';
import { SLUG_PATTERN } from '@shared/routes';
import { NotFoundPage } from './NotFoundPage';
import { ConfigErrorPage } from './ConfigErrorPage';
import { StudioSplash } from './StudioSplash';
import { StudioContext, studioQuery } from './studio-data';

/** Applies the studio theme, loads the studio and registers its service worker. */
export function StudioRoot() {
  const { slug = '' } = useParams();
  const validSlug = SLUG_PATTERN.test(slug);
  const shell = useMemo(() => readShell(), []);
  const query = useQuery({ ...studioQuery(slug), enabled: validSlug && env.configured });
  const accent = query.data?.accentColor ?? (shell?.slug === slug ? shell.accent : undefined);
  const theme = useMemo(() => studioTheme(accent), [accent]);

  useEffect(() => {
    let robots = document.querySelector<HTMLMetaElement>('meta[name="robots"][data-runtime]');
    if (query.data?.isPreview) {
      if (!robots) {
        robots = document.createElement('meta');
        robots.name = 'robots';
        robots.dataset.runtime = 'true';
        document.head.appendChild(robots);
      }
      robots.content = 'noindex';
    } else {
      robots?.remove();
    }
  }, [query.data?.isPreview]);

  let content;
  if (!validSlug) content = <NotFoundPage />;
  else if (!env.configured) content = <ConfigErrorPage />;
  else if (query.data) {
    content = (
      <StudioContext.Provider value={query.data}>
        <WorkerRegistration slug={slug} />
        <Outlet />
      </StudioContext.Provider>
    );
  } else if (query.isPending) content = <StudioSplash name={shell?.slug === slug ? shell.name : undefined} />;
  else if (toApiError(query.error).code === 'tenant_not_found') content = <NotFoundPage />;
  else {
    content = (
      <StudioSplash name={shell?.slug === slug ? shell.name : undefined}>
        <ErrorState error={query.error} onRetry={() => void query.refetch()} title="Студия временно недоступна" />
      </StudioSplash>
    );
  }

  return (
    <Theme theme={theme} mode="dark">
      <LinkProvider component={RouterLink}>
        <ToastViewport position="topEnd" maxVisible={3}>
          {content}
        </ToastViewport>
      </LinkProvider>
    </Theme>
  );
}

function WorkerRegistration({ slug }: { slug: string }) {
  const toast = useToast();
  useEffect(() => {
    void registerStudioWorker(slug, (apply) => {
      toast({
        body: 'Доступна новая версия приложения',
        isAutoHide: false,
        uniqueID: 'sw-update',
        endContent: <Button label="Обновить" size="sm" variant="primary" onClick={apply} />,
      });
    });
  }, [slug, toast]);
  return null;
}
